import { Box, darken, lighten, useTheme } from '@material-ui/core';
import {
  ArrowForwardRounded,
  EmojiObjectsRounded,
  Keyboard,
  TouchAppTwoTone,
} from '@material-ui/icons';
import * as React from 'react';
import { useMemo } from 'react';
import { useSpring } from 'react-spring';
import { AnalysisChartLoading } from '../../../AnalysisChartLoading';
import { ProjectTypeGq } from '../../../../../graphql/projects/models/ProjectTypeGq';
import { projectsApiService } from '../../../../../graphql/projects/projectsApiService';
import { ChartProps } from '../../../AnalysisList';
import { internationalText } from '../../../internationalText';
import { adjustTextColor } from '../../../Palette/adjustTextColor';
import DataTimedOut, { didQueryTimeout } from '../../DataTimedOut';
import NotEnoughData from '../../NotEnoughData';
import { MarketDemandItem } from './MarketDemandItem';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';

export interface MarketDemandProps extends ChartProps {}

const MarketDemand: React.FC<MarketDemandProps> = ({
  project,
  analysisFilterState,
  setAnalysisFilterState,
  language,
  palette,
  skipAnimation,
  emptyOnFail,
}) => {
  // Adjust shown filters
  React.useEffect(
    () =>
      setAnalysisFilterState &&
      setAnalysisFilterState((prev) => ({
        ...prev,
        shownFields: {
          showSerpOnly: true,
          showTargetRankValues: true,
          showTagGroupId: project.type === ProjectTypeGq.NORMAL_SEARCH ? true : false,
          showTagId: project.type === ProjectTypeGq.NORMAL_SEARCH ? true : false,
        },
      })),
    []
  );

  // Data fetching
  const { result: marketDemand, query } = projectsApiService.queries.analysis.useGetMarketPotential(
    {
      projectId: project.id,
      childProjectIds: project.childProjects.map((p) => p.childProjectId),
      serpOnly: !!analysisFilterState?.serpOnly,
      rankValues: analysisFilterState?.targetRankValues ?? [1],
      tagGroupId: analysisFilterState?.tagGroupId,
      tagId: analysisFilterState?.tagId,
    }
  );

  const normalNumbers = useMemo(
    () => ({
      keywordCount: marketDemand?.projectKeywordCount ?? 0,
      searchVolume: marketDemand?.projectSearchVolume ?? 0,
      trafficPotential: marketDemand?.projectTrafficPotential ?? 0,
    }),
    [marketDemand]
  );

  const localSkipAnimation = sessionStorage.getItem('skip_animation') || skipAnimation; // Setting for skipping spring when screenshotting
  const springNumbers = useSpring({
    ...normalNumbers,
    from: {
      keywordCount: 0,
      searchVolume: 0,
      trafficPotential: 0,
    },
    immediate: localSkipAnimation ? true : false,
  });

  // Styling
  const textColor = adjustTextColor(palette, 0.85, 1);

  if (didQueryTimeout(query)) {
    return emptyOnFail ? null : <DataTimedOut />;
  }

  if (query?.loading) {
    return <AnalysisChartLoading />;
  }

  if (!(marketDemand?.projectSearchVolume > 0)) {
    return emptyOnFail ? null : <NotEnoughData />;
  }

  return (
    <div data-screenshot-id="chart-loaded">
      <div className="flex flex-row justify-around items-end h-[500px] pb-8">
        <MarketDemandItem
          label={internationalText.chart.market_demand.keywords.label[language]}
          number={springNumbers.keywordCount}
          icon={EmojiObjectsRounded}
          description={internationalText.chart.market_demand.keywords.description[language]}
          bgColor={palette.secondaryScale[0]}
          iconColor={textColor}
          slideTheme={analysisFilterState?.theme}
        />
        <FontAwesomeIcon icon={faArrowRight} className="text-gray-300 text-5xl mb-44" />
        <MarketDemandItem
          label={internationalText.chart.market_demand.searches.label[language]}
          number={springNumbers.searchVolume}
          icon={Keyboard}
          description={internationalText.chart.market_demand.searches.description[language]}
          bgColor={palette.secondaryScale[1]}
          iconColor={textColor}
          slideTheme={analysisFilterState?.theme}
        />
        <FontAwesomeIcon icon={faArrowRight} className="text-gray-300 text-5xl mb-44" />
        <MarketDemandItem
          label={internationalText.chart.market_demand.traffic.label[language]}
          number={springNumbers.trafficPotential}
          icon={TouchAppTwoTone}
          description={internationalText.chart.market_demand.traffic.description[language]}
          bgColor={palette.secondaryScale[2]}
          iconColor={textColor}
          slideTheme={analysisFilterState?.theme}
        />
      </div>
    </div>
  );
};

export default MarketDemand;
